import React, { useEffect, useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { Role, RoleFormData, roleSchema } from "../../schemas/roleSchema";
import { useAllPermissions } from "../../hooks/useAllPermissions";
import { useAuth } from "../../hooks/useAuth";
import Loader from "../common/Loader";
import { toReadableCapitalized } from "../../utils/stringUtils";

interface RoleFormProps {
  isOpen: boolean;
  initialData?: Role;
  onSubmit: (data: RoleFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const RoleForm: React.FC<RoleFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      permissionIds: initialData?.permissions?.map((p) => p.id) || [],
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
  } = form;

  const { profile } = useAuth();

  // The matrix needs every permission — a permission the form cannot show is
  // one nobody can be granted — so this uses the unpaginated query. Asking the
  // paginated one for a page of 1000 quietly got 100: normalizePagination
  // clamps pageSize.
  const { permissions, isLoading: isPermissionLoading } = useAllPermissions();

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    initialData?.permissions?.map((p) => p.id) || [],
  );

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  const actions = ["view", "create", "update", "delete"];

  // Group permissions by module and organize into matrix format
  const permissionMatrix = useMemo(() => {
    const list = permissions;
    const userRole = profile?.role;
    const isSuperAdmin = userRole === "Super Admin";

    // Filter permissions based on user role
    const filtered = isSuperAdmin
      ? list
      : list.filter(
          (i: any) =>
            !i.action.includes("tenants") &&
            !i.action.includes("permissions") &&
            !i.action.includes("roles") &&
            !i.action.includes("pi_ota"),
        );

    // Group by module
    const moduleMap = new Map<
      string,
      Map<string, { id: string; action: string }>
    >();

    filtered.forEach((permission: any) => {
      const [module, action] = permission.action.split(".");
      if (!module || !action) return;

      if (!moduleMap.has(module)) {
        moduleMap.set(module, new Map());
      }

      const actionMap = moduleMap.get(module)!;
      actionMap.set(action, permission);
    });

    // Convert to array format for rendering
    const matrix: Array<{
      module: string;
      permissions: Map<string, { id: string; action: string }>;
    }> = [];

    moduleMap.forEach((actionMap, module) => {
      matrix.push({ module, permissions: actionMap });
    });

    // Sort modules alphabetically
    matrix.sort((a, b) => a.module.localeCompare(b.module));

    return matrix;
  }, [permissions, profile?.role]);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        const permissionIds = initialData.permissions?.map((p) => p.id) || [];
        reset({
          name: initialData.name || "",
          description: initialData.description || "",
          permissionIds: permissionIds,
        });
        setSelectedPermissions(permissionIds);
      } else {
        reset({
          name: "",
          description: "",
          permissionIds: [],
        });
        setSelectedPermissions([]);
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  const handleFormSubmit = async (data: RoleFormData) => {
    const formData = {
      ...data,
      permissionIds: selectedPermissions,
    };
    await onSubmit(formData);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions((prev) => {
      const updated = prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId];

      // Sync with react-hook-form
      setValue("permissionIds", updated, { shouldValidate: true });
      return updated;
    });
  };

  const handleModuleSelectAll = (
    module: string,
    modulePermissions: Map<string, { id: string; action: string }>,
  ) => {
    const modulePermissionIds: string[] = [];
    actions.forEach((action) => {
      const permission = modulePermissions.get(action);
      if (permission) {
        modulePermissionIds.push(permission.id);
      }
    });

    // Check if all actions for this module are selected
    const allSelected = modulePermissionIds.every((id) =>
      selectedPermissions.includes(id),
    );

    setSelectedPermissions((prev) => {
      let updated: string[];
      if (allSelected) {
        // Deselect all actions for this module
        updated = prev.filter((id) => !modulePermissionIds.includes(id));
      } else {
        // Select all actions for this module
        updated = [...prev];
        modulePermissionIds.forEach((id) => {
          if (!updated.includes(id)) {
            updated.push(id);
          }
        });
      }

      // Sync with react-hook-form
      setValue("permissionIds", updated, { shouldValidate: true });
      return updated;
    });
  };

  const handleSelectAll = () => {
    const allPermissionIds: string[] = [];
    permissionMatrix.forEach(({ permissions }) => {
      permissions.forEach((permission) => {
        allPermissionIds.push(permission.id);
      });
    });
    setSelectedPermissions(allPermissionIds);
    setValue("permissionIds", allPermissionIds, { shouldValidate: true });
  };

  const handleClearAll = () => {
    setSelectedPermissions([]);
    setValue("permissionIds", [], { shouldValidate: true });
  };

  const isModuleAllSelected = (
    modulePermissions: Map<string, { id: string; action: string }>,
  ): boolean => {
    const modulePermissionIds: string[] = [];
    actions.forEach((action) => {
      const permission = modulePermissions.get(action);
      if (permission) {
        modulePermissionIds.push(permission.id);
      }
    });

    if (modulePermissionIds.length === 0) return false;
    return modulePermissionIds.every((id) => selectedPermissions.includes(id));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? "Edit Role" : "Create Role"}
      size="full"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <Input
          label="Role Name"
          placeholder="Enter role name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) =>
            setValue("name", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("name")}
          error={errors.name}
        />
        <Input
          label="Description"
          placeholder="Enter role description (optional)"
          name="description"
          value={watch("description") || ""}
          onChange={(e) =>
            setValue("description", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("description")}
          error={errors.description}
        />

        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Permissions</h3>
            <div className="space-x-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            </div>
          </div>

          {errors.permissionIds && (
            <p className="text-red-600 mb-3 text-sm">
              {errors.permissionIds.message}
            </p>
          )}

          <div className="max-h-96 overflow-x-auto border border-gray-200 rounded-lg">
            {isPermissionLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader />
              </div>
            ) : permissionMatrix.length > 0 ? (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10">
                      Module
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Select All
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      View
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Create
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Update
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {permissionMatrix.map(({ module, permissions }) => {
                    const moduleAllSelected = isModuleAllSelected(permissions);
                    return (
                      <tr
                        key={module}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                          {toReadableCapitalized(module)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={moduleAllSelected}
                            onChange={() =>
                              handleModuleSelectAll(module, permissions)
                            }
                            className="h-4 w-4 text-primary-default focus:ring-primary-default border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                        {actions.map((action) => {
                          const permission = permissions.get(action);
                          const isSelected = permission
                            ? selectedPermissions.includes(permission.id)
                            : false;

                          return (
                            <td
                              key={action}
                              className="border border-gray-200 px-4 py-3 text-center"
                            >
                              {permission ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    handlePermissionToggle(permission.id)
                                  }
                                  className="h-4 w-4 text-primary-default focus:ring-primary-default border-gray-300 rounded cursor-pointer"
                                />
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No permissions available. Please create some permissions first.
              </div>
            )}
          </div>

          {selectedPermissions.length > 0 && (
            <div className="mt-3 text-sm text-gray-600">
              Selected: {selectedPermissions.length} permission(s)
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            {initialData ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RoleForm;
